// API接口统一管理
import request from '@/utils/request'
import { storage, STORAGE_KEYS } from '@/utils/storage'
import { BASE_URL, API_ENDPOINTS } from '@/config/index'

// API接口版本
const API_VERSION = 'v1'

// 通用CRUD操作
export const crud = {
  // 创建
  create: (resource, data) => request({
    url: `${BASE_URL}/${resource}`,
    method: 'post',
    data
  }),

  // 读取列表
  list: (resource, params) => request({
    url: `${BASE_URL}/${resource}`,
    method: 'get',
    params
  }),

  // 读取详情
  detail: (resource, id) => request({
    url: `${BASE_URL}/${resource}/${id}`,
    method: 'get'
  }),

  // 更新
  update: (resource, id, data) => request({
    url: `${BASE_URL}/${resource}/${id}`,
    method: 'put',
    data
  }),

  // 删除
  delete: (resource, id) => request({
    url: `${BASE_URL}/${resource}/${id}`,
    method: 'delete'
  }),

  // 批量删除
  batchDelete: (resource, ids) => request({
    url: `${BASE_URL}/${resource}/batch`,
    method: 'delete',
    data: { ids }
  }),

  // 批量更新
  batchUpdate: (resource, data) => request({
    url: `${BASE_URL}/${resource}/batch`,
    method: 'put',
    data
  })
}

// 数据持久化相关操作
export const storageOps = {
  // 同步数据到本地
  syncToLocal: async (key, fetchFunc) => {
    try {
      const res = await fetchFunc()
      if (res?.data?.code === 200) {
        storage.set(key, res.data.data)
        return res.data.data
      }
      return null
    } catch (error) {
      console.error(`Sync data error for key ${key}:`, error)
      return null
    }
  },

  // 从本地获取数据，如果没有则从服务器获取
  getWithCache: async (key, fetchFunc) => {
    const localData = storage.get(key)
    if (localData) {
      return localData
    }
    return await storageOps.syncToLocal(key, fetchFunc)
  }
}

// 文件上传相关
export const upload = {
  // 上传单个文件
  single: (file, params = {}) => {
    const formData = new FormData()
    formData.append('file', file)
    Object.entries(params).forEach(([key, value]) => {
      formData.append(key, value)
    })

    return request({
      url: `${BASE_URL}/upload`,
      method: 'post',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  },

  // 批量上传文件
  multiple: (files, params = {}) => {
    const formData = new FormData()
    files.forEach(file => {
      formData.append('files[]', file)
    })
    Object.entries(params).forEach(([key, value]) => {
      formData.append(key, value)
    })

    return request({
      url: `${BASE_URL}/upload/batch`,
      method: 'post',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }
}

// 导出相关
export const exportData = {
  // 导出Excel
  excel: (resource, params) => request({
    url: `${BASE_URL}/${resource}/export/excel`,
    method: 'get',
    params,
    responseType: 'blob'
  }),

  // 导出PDF
  pdf: (resource, params) => request({
    url: `${BASE_URL}/${resource}/export/pdf`,
    method: 'get',
    params,
    responseType: 'blob'
  })
}

// 导入相关
export const importData = {
  // 导入Excel
  excel: (resource, file, params = {}) => {
    const formData = new FormData()
    formData.append('file', file)
    Object.entries(params).forEach(([key, value]) => {
      formData.append(key, value)
    })

    return request({
      url: `${BASE_URL}/${resource}/import/excel`,
      method: 'post',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }
}

// 系统相关API
export const system = {
  // 获取系统配置
  getConfig: () => request({
    url: `${BASE_URL}/system/config`,
    method: 'get'
  }),

  // 更新系统配置
  updateConfig: (data) => request({
    url: `${BASE_URL}/system/config`,
    method: 'put',
    data
  }),

  // 获取系统状态
  getStatus: () => request({
    url: `${BASE_URL}/system/status`,
    method: 'get'
  }),

  // 获取系统日志
  getLogs: (params) => request({
    url: `${BASE_URL}/system/logs`,
    method: 'get',
    params
  })
}

// 用户相关API
export const user = {
  // 获取用户信息
  getInfo: () => request({
    url: `${BASE_URL}/account/info`,
    method: 'get'
  }),

  // 更新用户信息
  updateInfo: (data) => request({
    url: `${BASE_URL}/account/update`,
    method: 'post',
    data
  }),

  // 修改密码
  changePassword: (data) => request({
    url: `${BASE_URL}/account/password`,
    method: 'post',
    data
  })
}

// 统计相关API
export const statistics = {
  // 获取概览数据
  getOverview: () => {
    const data = storage.getStatisticsOverview()
    return Promise.resolve({
      data: {
        code: 200,
        message: '获取成功',
        data: data
      }
    })
  },

  // 获取趋势数据
  getTrend: (params) => {
    const data = storage.getStatisticsTrend(params.days)
    return Promise.resolve({
      data: {
        code: 200,
        message: '获取成功',
        data: data
      }
    })
  },

  // 获取排行数据
  getRanking: (params) => {
    const data = storage.getStatisticsRanking(params.type)
    return Promise.resolve({
      data: {
        code: 200,
        message: '获取成功',
        data: data
      }
    })
  },

  // 获取系统状态
  getStatus: () => {
    const data = storage.getSystemStatus()
    return Promise.resolve({
      data: {
        code: 200,
        message: '获取成功',
        data: data
      }
    })
  }
}

// 认证相关API
export const auth = {
  // 登录
  login: (data) => request({
    url: `${BASE_URL}/auth/login`,
    method: 'post',
    data
  }),

  // 登出
  logout: () => request({
    url: `${BASE_URL}/auth/logout`,
    method: 'post'
  }),

  // 获取用户信息
  getUserInfo: () => request({
    url: `${BASE_URL}/auth/user-info`,
    method: 'get'
  })
}

// 授权列表相关API
export const authList = {
  // 获取授权列表
  getList: (params) => request({
    url: `${BASE_URL}/auth/list`,
    method: 'get',
    params
  }),

  // 添加授权
  add: (data) => request({
    url: `${BASE_URL}/auth/create`,
    method: 'post',
    data
  }),

  // 更新授权
  update: (data) => request({
    url: `${BASE_URL}/auth/update`,
    method: 'put',
    data
  }),

  // 删除授权
  delete: (data) => request({
    url: `${BASE_URL}/auth/delete`,
    method: 'delete',
    data
  }),

  // 续期授权
  renew: (data) => request({
    url: `${BASE_URL}/auth/renew`,
    method: 'post',
    data
  })
}

// 回收站相关API
export const recycleBin = {
  // 获取回收站列表
  getList: (params) => request({
    url: `${BASE_URL}/recycle/list`,
    method: 'get',
    params
  }),

  // 恢复项目
  restore: (data) => request({
    url: `${BASE_URL}/recycle/restore`,
    method: 'post',
    data
  }),

  // 永久删除
  delete: (data) => request({
    url: `${BASE_URL}/recycle/permanent-delete`,
    method: 'delete',
    data
  }),

  // 批量恢复
  batchRestore: (data) => request({
    url: `${BASE_URL}/recycle/batch-restore`,
    method: 'post',
    data
  }),

  // 批量永久删除
  batchDelete: (data) => request({
    url: `${BASE_URL}/recycle/batch-permanent-delete`,
    method: 'delete',
    data
  }),

  // 清空回收站
  clear: () => request({
    url: `${BASE_URL}/recycle/clear`,
    method: 'delete'
  }),

  // 自动清理
  autoClean: () => request({
    url: `${BASE_URL}/recycle/auto-clean`,
    method: 'post'
  })
}

// APP授权相关API
export const appAuth = {
  // 获取APP授权列表
  getList: (params) => request({
    url: `${BASE_URL}/app-auth/list`,
    method: 'get',
    params
  }),

  // 添加APP授权
  add: (data) => request({
    url: `${BASE_URL}/app-auth/generate`,
    method: 'post',
    data
  }),

  // 更新APP授权
  update: (id, data) => request({
    url: `${BASE_URL}/app-auth/update`,
    method: 'put',
    data
  }),

  // 删除APP授权
  delete: (id) => request({
    url: `${BASE_URL}/app-auth/delete`,
    method: 'delete'
  })
}

// 代码管理相关API
export const code = {
  // 前端代码管理
  frontend: {
    getList: (params) => request({
      url: `${BASE_URL}/code/frontend`,
      method: 'get',
      params
    }),
    add: (data) => request({
      url: `${BASE_URL}/code/frontend`,
      method: 'post',
      data
    }),
    update: (id, data) => request({
      url: `${BASE_URL}/code/frontend/${id}`,
      method: 'put',
      data
    }),
    delete: (id) => request({
      url: `${BASE_URL}/code/frontend/${id}`,
      method: 'delete'
    })
  },

  // 后端代码管理
  backend: {
    getList: (params) => request({
      url: `${BASE_URL}/code/backend`,
      method: 'get',
      params
    }),
    add: (data) => request({
      url: `${BASE_URL}/code/backend`,
      method: 'post',
      data
    }),
    update: (id, data) => request({
      url: `${BASE_URL}/code/backend/${id}`,
      method: 'put',
      data
    }),
    delete: (id) => request({
      url: `${BASE_URL}/code/backend/${id}`,
      method: 'delete'
    })
  }
}

// 服务相关API
export const services = {
  // SMS服务
  sms: {
    getList: (params) => request({
      url: `${BASE_URL}/services/sms`,
      method: 'get',
      params
    }),
    add: (data) => request({
      url: `${BASE_URL}/services/sms`,
      method: 'post',
      data
    }),
    update: (id, data) => request({
      url: `${BASE_URL}/services/sms/${id}`,
      method: 'put',
      data
    }),
    delete: (id) => request({
      url: `${BASE_URL}/services/sms/${id}`,
      method: 'delete'
    })
  },

  // 保险服务
  insurance: {
    getList: (params) => request({
      url: `${BASE_URL}/services/insurance`,
      method: 'get',
      params
    }),
    add: (data) => request({
      url: `${BASE_URL}/services/insurance`,
      method: 'post',
      data
    }),
    update: (id, data) => request({
      url: `${BASE_URL}/services/insurance/${id}`,
      method: 'put',
      data
    }),
    delete: (id) => request({
      url: `${BASE_URL}/services/insurance/${id}`,
      method: 'delete'
    })
  }
}

// 服务商管理相关API
export const provider = {
  // 服务商接入：读取第三方平台配置
  getConfig: () => request({
    url: `${BASE_URL}/provider/config`,
    method: 'get'
  }),

  // 服务商接入：保存第三方平台配置
  saveConfig: (data) => request({
    url: `${BASE_URL}/provider/config`,
    method: 'post',
    data
  }),

  // 一键生成 Token / EncodingAESKey
  genKeys: () => request({
    url: `${BASE_URL}/provider/gen-keys`,
    method: 'post'
  }),

  // 服务商资源
  getResources: () => request({
    url: `${BASE_URL}/provider/resources`,
    method: 'get'
  }),

  // 保存发布默认设置
  savePublishDefaults: (data) => request({
    url: `${BASE_URL}/provider/publish-defaults`,
    method: 'post',
    data
  }),

  // 小程序代码：草稿箱 + 模板库 + 当前模板
  getWxaCode: () => request({
    url: `${BASE_URL}/provider/wxa/code`,
    method: 'get'
  }),

  // 草稿加入模板库并设为当前
  wxaAddToTemplate: (data) => request({
    url: `${BASE_URL}/provider/wxa/code/add-to-template`,
    method: 'post',
    data
  }),

  // 设为当前模板
  wxaSetCurrent: (data) => request({
    url: `${BASE_URL}/provider/wxa/code/set-current`,
    method: 'post',
    data
  }),

  // 删除模板
  wxaDeleteTemplate: (data) => request({
    url: `${BASE_URL}/provider/wxa/code/delete-template`,
    method: 'post',
    data
  }),

  // 小程序授权列表（可按域名过滤）
  getMpAuthList: (params) => request({
    url: `${BASE_URL}/provider/mp-auth/list`,
    method: 'get',
    params
  }),

  // 授权记录里的域名列表（搜索下拉用）
  mpAuthDomains: () => request({
    url: `${BASE_URL}/provider/mp-auth/domains`,
    method: 'get'
  }),

  // 按域名查该域名下的平台（含授权状态）
  mpAuthPlatforms: (data) => request({
    url: `${BASE_URL}/provider/mp-auth/platforms`,
    method: 'post',
    data
  }),

  // 生成某个平台的微信授权链接
  mpAuthAuthUrl: (data) => request({
    url: `${BASE_URL}/provider/mp-auth/auth-url`,
    method: 'post',
    data
  }),

  // 取消授权
  mpAuthRevoke: (data) => request({
    url: `${BASE_URL}/provider/mp-auth/revoke`,
    method: 'post',
    data
  }),

  // 探测服务器出口 IP（微信开放平台白名单要用）
  detectIp: (refresh = false) => request({
    url: `${BASE_URL}/provider/detect-ip`,
    method: 'post',
    data: { refresh }
  }),

  // 连接自检
  selfCheck: () => request({
    url: `${BASE_URL}/provider/self-check`,
    method: 'post'
  }),

  // 微信请求 / 事件日志
  getEventLogs: () => request({
    url: `${BASE_URL}/provider/event-logs`,
    method: 'get'
  })
}

// 设置相关API
export const settings = {
  // 获取站点设置
  getSiteSettings: () => request({
    url: `${BASE_URL}/settings/site`,
    method: 'get'
  }),

  // 更新站点设置
  updateSiteSettings: (data) => request({
    url: `${BASE_URL}/settings/site`,
    method: 'put',
    data
  })
}

export default {
  crud,
  storageOps,
  upload,
  exportData,
  importData,
  system,
  user,
  statistics,
  auth,
  authList,
  recycleBin,
  appAuth,
  code,
  services,
  provider,
  settings
}